import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { usePOCreation } from '../contextProvider/POCreationContext';
import { imageUrl } from '../../../../Components/helper';

export default function PODetailsForPO({ isPreviewMode = false, isEditMode = false }) {
  const { t } = useTranslation('purchase-order');
  const { creationState, handlePOFieldChange, companyLogo, isFieldsDisabledByStatus } = usePOCreation();

  const {
    BOMConfigDetails: { isProductArchived },
    UserDetails: { isSupplierLoggedIn },
  } = useSelector((reducer) => reducer);

  const imgSrc = (img) => {
    const imageToUse = img || companyLogo;
    if (!imageToUse) return companyLogo;
    if (imageToUse?.includes('https://') || imageToUse?.includes('base64')) {
      return imageToUse;
    }
    if (imageToUse?.includes('super-admin')) {
      return `https://${process.env.REACT_APP_BUCKET_NAME}.s3.${process.env.REACT_APP_AWS_REGION}.amazonaws.com/${imageToUse}`;
    }
    if (imageToUse?.includes('s3.eu-west-2')) {
      return imageToUse;
    }
    if (imageToUse?.startsWith('http') || imageToUse?.startsWith('https')) {
      const href = new URL(imageToUse);
      return `${imageUrl}${href.pathname}`;
    }
    return `https://${process.env.REACT_APP_BUCKET_NAME}.s3.${process.env.REACT_APP_AWS_REGION}.amazonaws.com/${imageToUse}`;
  };

  const formatDate = (date) => {
    if (!date) return '--';

    if (date instanceof Date) {
      return date.toLocaleDateString();
    }

    if (typeof date === 'string') {
      const parsedDate = new Date(date);
      if (!Number.isNaN(parsedDate.getTime())) {
        return parsedDate.toLocaleDateString();
      }
    }
    return String(date);
  };

  const handleOnChange = (value, field) => {
    handlePOFieldChange(field, value);
  };

  return (
    <div className="inner-container po-template-name p-24 mb-8">
      <div className="row">
        <div className="col-md-4">
          <h3 className="main-title">{t('CREATE_EDIT_PO.PURCHASE_ORDER')}</h3>
          <div className="form-group">
            <div className="common-input-control">
              <p className="field-name">
                {t('CREATE_EDIT_PO.PO_NUMBER')} <span className="asterisk">*</span>
              </p>
              <input
                type="text"
                name="purchaseOrderNumber"
                className="form-control"
                value={creationState.purchaseOrderNumber}
                onChange={(e) => handleOnChange(e.target.value, 'purchaseOrderNumber')}
                disabled={isPreviewMode || isProductArchived || isSupplierLoggedIn || isFieldsDisabledByStatus() || isEditMode}
              />
              {creationState.validationErrors.purchaseOrderNumber && (
                <div className="invalid-feedback d-block">{creationState.validationErrors.purchaseOrderNumber}</div>
              )}
            </div>
          </div>
          <div className="d-flex">
            <div className="mr-16 f-18 text-nowrap">
              <span className="text-medium">{t('CREATE_EDIT_PO.CREATED_DATE')}: </span>
              <span>{formatDate(creationState?.POCreatedDate)}</span>
            </div>
            <div className="mr-16 f-18 text-nowrap">
              <span className="text-medium">{t('CREATE_EDIT_PO.SUBMITTED_DATE')}: </span>
              <span>{formatDate(creationState?.POSubmittedDate)}</span>
            </div>
            <div className="mr-16 f-18 text-nowrap">
              <span className="text-medium">{t('CREATE_EDIT_PO.REVISED_DATE')}: </span>
              <span>{formatDate(creationState?.PORevisedDate)}</span>
            </div>
          </div>
        </div>
        <div className="col-md-4 offset-md-4 d-flex align-items-center justify-content-end">
          <figure className="mb-0">
            <img src={imgSrc(creationState?.companyLogo)} className="img-fluid po-temp-image" alt={t('CREATE_EDIT_PO.COMPANY_LOGO')} />
          </figure>
        </div>
      </div>
    </div>
  );
}

PODetailsForPO.propTypes = {
  isPreviewMode: PropTypes.bool,
  isEditMode: PropTypes.bool,
};

PODetailsForPO.defaultProps = {
  isPreviewMode: false,
  isEditMode: false,
};

PODetailsForPO.displayName = 'PODetailsForPO';
